// @cSpell:ignore unsubs
import { fontAwesome } from '@treasury/omega/css/icons/font-awesome.js';
import { TmBaseComponent } from '@treasury/presentation';
import { type Action } from '@treasury/utils';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export const tagName = 'jhd-tooltip';
@customElement(tagName)
export class JhdTooltip extends TmBaseComponent {
    @property({ type: Boolean, reflect: true, attribute: 'is-clickable' })
    public isClickable = false;

    @property({ type: String })
    public direction = 'top';

    @property({ type: String })
    public icon = '';

    @property({ type: Boolean, reflect: true })
    public light = false;

    @property({ type: String })
    public message = '';

    @property({ type: Boolean, reflect: true })
    public show = false;

    @property({ type: String })
    public class = '';

    @state()
    private tooltipBody?: HTMLElement;

    @state()
    private relativePosition: DOMRect = {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        top: 0,
        right: 0,
        bottom: 0,
        left: 0,
        toJSON: () => '',
    };

    @state()
    private width = 0;

    @state()
    private height = 0;

    /**
     * Array of event handler unsubscribe functions.
     */
    private unsubs: Action[] = [];

    public static get meta() {
        return {
            docUrl: 'https://banno.github.io/treasury-management/?path=/docs/components-toggle--toggle',
        };
    }

    protected firstUpdated() {
        this.tooltipBody = this.shadowRoot?.querySelector('.jhd-tooltip') || undefined;
        this.setDimensions();
        this.relativePosition = this.getBoundingClientRect();
        this.setTooltipClass();
        if (this.isClickable) {
            this.addEventListener('click', this.showTooltip);
        }
    }

    protected render() {
        return html`<span>
            ${this.renderIcon()} ${this.renderTooltipTrigger()}
            <div class=${this.class}>${this.renderTooltipContent()}</div>
        </span>`;
    }

    private toggleShow() {
        if (this.isClickable) {
            this.show = !this.show;
        }

        this.addOrRemoveListeners(this.show);
    }

    private addOrRemoveListeners(show: boolean) {
        if (show) {
            this.focus();
            const keydownHandler = (e: KeyboardEvent) => this.listenForKeydown(e);
            const clickHandler = (e: MouseEvent) => this.listenForDocumentClicks(e);
            document.addEventListener('keydown', keydownHandler);
            document.addEventListener('click', clickHandler);

            this.unsubs.push(
                () => document.removeEventListener('keydown', keydownHandler),
                () => document.removeEventListener('click', clickHandler)
            );
        } else {
            this.unsubs.forEach(unsub => unsub());
            this.unsubs = [];
        }
    }

    private listenForDocumentClicks(e: MouseEvent) {
        const shouldClose = !(e.composedPath() || []).includes(this);

        if (shouldClose) {
            e.preventDefault();
            this.hideTooltip();
        }
    }

    private listenForKeydown(e: KeyboardEvent) {
        if (e.keyCode === 27) {
            this.hideTooltip();
        }
    }

    private showTooltip() {
        if (this.isClickable) {
            this.show = true;
            this.addOrRemoveListeners(this.show);
            this.removeEventListener('click', this.showTooltip);
        }
    }

    private hideTooltip() {
        if (this.isClickable) {
            this.show = false;
            window.requestAnimationFrame(() => {
                this.addEventListener('click', this.showTooltip);
            });
        }
    }

    private setDimensions() {
        this.height =
            parseInt(
                window.getComputedStyle(this.tooltipBody || document.createElement('div')).height,
                10
            ) +
            parseInt(
                window.getComputedStyle(this.tooltipBody || document.createElement('div'))
                    .paddingTop,
                10
            ) +
            parseInt(
                window.getComputedStyle(this.tooltipBody || document.createElement('div'))
                    .paddingBottom,
                10
            );
        this.width =
            parseInt(
                window.getComputedStyle(this.tooltipBody || document.createElement('div')).width,
                10
            ) +
            parseInt(
                window.getComputedStyle(this.tooltipBody || document.createElement('div'))
                    .paddingRight,
                10
            ) +
            parseInt(
                window.getComputedStyle(this.tooltipBody || document.createElement('div'))
                    .paddingLeft,
                10
            );
    }

    private setTooltipClass() {
        const direction = this.setTooltipDirection();
        const brightness = this.light ? 'tooltip-light' : '';
        this.class = `jhd-tooltip ${brightness} ${direction}`;
    }

    private setTooltipDirection() {
        if (this.direction === undefined) {
            return this.calcTooltipDirection();
        }
        if (this.direction.trim().length === 0) {
            return this.calcTooltipDirection();
        }
        return `tooltip-${this.direction}`;
    }

    private calcTooltipDirection() {
        if (this.height > this.relativePosition.y) {
            return 'tooltip-bottom';
        }
        if (this.width / 2 > this.relativePosition.x) {
            return 'tooltip-right';
        }
        if (this.width > window.innerWidth - this.relativePosition.x) {
            return 'tooltip-left';
        }
        return 'tooltip-top';
    }

    private renderIcon() {
        if (!this.icon) {
            return nothing;
        }
        return html`<i
            class="jhd-tooltip-icon fa fa-${this.icon}"
            @click=${this.toggleShow}
            @keypress=${(e: KeyboardEvent) => {
                if (e.key === 'Enter') {
                    this.toggleShow();
                }
            }}
        ></i>`;
    }

    private renderTooltipContent() {
        if (this.message) {
            return html`${this.message}`;
        }
        if (!this.isClickable) {
            return html`<div>
                <slot name="content"></slot>
            </div> `;
        }
        return html`<div>
            <button
                id="tooltip-close-button"
                class="jhd-tooltip-close-button"
                @click=${() => this.hideTooltip()}
            >
                <i class="fa fa-times jhd-tooltip-close" role="button" aria-label="Close"></i>
            </button>
            <slot name="content"></slot>
        </div>`;
    }

    private renderTooltipTrigger() {
        if (this.isClickable) {
            return html`<button id="tooltip-trigger" @click=${this.toggleShow}>
                <slot></slot>
            </button>`;
        }
        return html`<div id="tooltip-trigger">
            <slot></slot>
        </div>`;
    }

    public static get styles() {
        return [
            fontAwesome,
            css`
                .isClickable {
                    cursor: pointer;
                }
                :host {
                    --jhd-tooltip-transition: all 0.1s cubic-bezier(0.84, -0.18, 0.65, 1.13);
                    --jhd-tooltip-background-color: var(--omega-secondary);
                }
                :host([light]) {
                    --jhd-tooltip-background-color: var(--container-primary);
                }
                :host([icon]) {
                    display: inline-block;
                }

                .jhd-tooltip-icon {
                    font-size: 16px;
                    color: var(--omega-secondary-lighten-100);
                }

                span {
                    display: var(--tooltip-span-display, flex);
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    white-space: nowrap;
                    min-width: 25px;
                }

                #tooltip-trigger {
                    background: none;
                    border: none;
                    margin: 0;
                    padding: 0;
                }

                .jhd-tooltip.tooltip-light {
                    --jhd-tooltip-background-color: var(--container-primary);
                    color: var(--omega-text-default);
                    border-radius: 7px;
                    filter: drop-shadow(0 0 5px var(--omega-secondary-lighten-200));
                }

                .jhd-tooltip {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    position: relative;
                    text-align: left;
                    white-space: normal;
                }

                .jhd-tooltip:focus-within {
                    outline: none;
                }

                .jhd-tooltip::before,
                .jhd-tooltip {
                    opacity: 0;
                    position: absolute;
                    z-index: -100;
                }

                .jhd-tooltip::before {
                    border-style: solid;
                    border-width: 16px 12px 0 12px;
                    border-color: var(--jhd-tooltip-background-color) transparent transparent
                        transparent;
                    content: '';
                    transition: var(--tooltip-transition);
                    bottom: -8px;
                }

                .jhd-tooltip.tooltip-bottom-right::before,
                .jhd-tooltip.tooltip-bottom-left::before,
                .jhd-tooltip.tooltip-bottom::before {
                    border-width: 0 12px 16px 12px;
                    border-color: transparent transparent var(--jhd-tooltip-background-color)
                        transparent;
                    top: -8px;
                    bottom: auto;
                }

                .jhd-tooltip.tooltip-top-right::before,
                .jhd-tooltip.tooltip-bottom-right::before {
                    left: 3px;
                }
                .jhd-tooltip.tooltip-top-left::before,
                .jhd-tooltip.tooltip-bottom-left::before {
                    right: 3px;
                }

                .jhd-tooltip.tooltip-right-bottom::before,
                .jhd-tooltip.tooltip-right-top::before,
                .jhd-tooltip.tooltip-right::before {
                    border-width: 12px 16px 12px 0;
                    border-color: transparent var(--jhd-tooltip-background-color) transparent
                        transparent;
                    left: -8px;
                    bottom: auto;
                }

                .jhd-tooltip.tooltip-left-bottom::before,
                .jhd-tooltip.tooltip-left-top::before,
                .jhd-tooltip.tooltip-left::before {
                    border-width: 12px 0 12px 16px;
                    border-color: transparent transparent transparent
                        var(--jhd-tooltip-background-color);
                    right: -8px;
                    bottom: auto;
                }

                .jhd-tooltip.tooltip-right-top::before,
                .jhd-tooltip.tooltip-left-top::before {
                    top: var(--jhd-tooltip-indicator-top, 45px);
                }

                .jhd-tooltip.tooltip-right-bottom::before,
                .jhd-tooltip.tooltip-left-bottom::before {
                    bottom: 45px;
                    top: auto;
                }

                .jhd-tooltip {
                    background: var(--jhd-tooltip-background-color);
                    border-radius: 0.25em;
                    bottom: calc(100% + 12px);
                    color: var(--omega-info-lighten-300);
                    font-size: 12px;
                    padding: 16px;
                    transition: var(--tooltip-transition);
                    width: max-content;
                    visibility: hidden;
                }

                .jhd-tooltip.tooltip-bottom {
                    top: calc(100% + 12px);
                    bottom: auto;
                }

                .jhd-tooltip.tooltip-right-top,
                .jhd-tooltip.tooltip-right-bottom,
                .jhd-tooltip.tooltip-right {
                    left: calc(100% + 12px);
                    bottom: auto;
                }

                .jhd-tooltip.tooltip-left-bottom,
                .jhd-tooltip.tooltip-left-top,
                .jhd-tooltip.tooltip-left {
                    right: calc(100% + 12px);
                    bottom: auto;
                }

                .jhd-tooltip.tooltip-right-top,
                .jhd-tooltip.tooltip-left-top {
                    top: var(--jhd-tooltip-left-top-top, -50px);
                }

                .jhd-tooltip.tooltip-right-bottom,
                .jhd-tooltip.tooltip-left-bottom {
                    bottom: -50px;
                    top: auto;
                }

                .jhd-tooltip.tooltip-bottom-right {
                    left: 0;
                    top: calc(100% + 12px);
                    bottom: auto;
                }

                .jhd-tooltip.tooltip-top-right {
                    left: 0;
                }

                .jhd-tooltip.tooltip-top-left {
                    right: 0;
                }

                .jhd-tooltip.tooltip-bottom-left {
                    right: -4px;
                    top: calc(100% + 12px);
                    bottom: auto;
                }

                .jhd-tooltip-close-button {
                    position: absolute;
                    right: 10px;
                    top: 7px;
                    background: none;
                    border: none;
                    padding: 0;
                }
                .jhd-tooltip-close {
                    font-weight: normal;
                    color: var(--omega-text-tertiary);
                    cursor: pointer;
                    font-size: var(--omega-label);
                }

                :host([show]) .jhd-tooltip::before,
                :host([show]) .jhd-tooltip,
                :host(:not([isClickable]):hover) .jhd-tooltip::before,
                :host(:not([isClickable]):focus-within) .jhd-tooltip::before,
                :host(:not([isClickable]):hover) .jhd-tooltip,
                :host(:not([isClickable]):focus-within) .jhd-tooltip {
                    opacity: 1;
                    z-index: 100;
                    visibility: visible;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: JhdTooltip;
    }
}
